import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { NavigationEnd, Router, ActivatedRouteSnapshot } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbService } from '../../services/breadcrumb.service';

const BREADCRUMB_MODOS_LINK_ENTIDADE = {
  removerUltimoSegmento: true,
  detalheEmpresaPeloRouter: true,
  propostaDetalhesDesdeEdicao: true,
} as const;

type BreadcrumbModoLinkEntidade = keyof typeof BREADCRUMB_MODOS_LINK_ENTIDADE;

function isBreadcrumbModoLinkEntidade(
  v: unknown,
): v is BreadcrumbModoLinkEntidade {
  return (
    typeof v === 'string' && Object.hasOwn(BREADCRUMB_MODOS_LINK_ENTIDADE, v)
  );
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BreadcrumbComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breadcrumbService = inject(BreadcrumbService);

  items: MenuItem[] = [];

  constructor() {
    effect(() => {
      this.breadcrumbService.getDynamicLabels();
      this.atualizarBreadcrumbs();
    });
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.atualizarBreadcrumbs();
      });

    this.atualizarBreadcrumbs();
  }

  private atualizarBreadcrumbs(): void {
    const breadcrumbs = this.construirBreadcrumbs();
    this.marcarUltimoItem(breadcrumbs);
    this.atualizarEstado(breadcrumbs);
  }

  private construirBreadcrumbs(): MenuItem[] {
    const breadcrumbs: MenuItem[] = [];
    let currentRoute: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let url = '';

    while (currentRoute) {
      url = this.atualizarUrl(url, currentRoute);
      const data = currentRoute.routeConfig?.data;

      if (data) {
        this.processarModulo(data, url, breadcrumbs);
        this.processarBreadcrumb(data, url, breadcrumbs);
      }

      currentRoute = currentRoute.firstChild;
    }

    return breadcrumbs;
  }

  private atualizarUrl(url: string, route: ActivatedRouteSnapshot): string {
    const routeSegments = route.url.map(this.extrairPath).join('/');
    return routeSegments ? `${url}/${routeSegments}` : url;
  }

  private extrairPath(segment: { path: string }): string {
    return segment.path;
  }

  private processarModulo(
    data: Record<string, unknown>,
    url: string,
    breadcrumbs: MenuItem[],
  ): void {
    const moduloLabel = data['modulo'] as string;
    if (!moduloLabel) {
      return;
    }

    if (this.breadcrumbJaExiste(breadcrumbs, moduloLabel)) {
      return;
    }

    breadcrumbs.push({
      label: moduloLabel,
      routerLink: this.obterUrlModulo(url),
      data: { showFullLabel: data['showFullLabel'] === true },
    });
  }

  private processarBreadcrumb(
    data: Record<string, unknown>,
    url: string,
    breadcrumbs: MenuItem[],
  ): void {
    const breadcrumbLabel = data['breadcrumb'] as string;
    if (!breadcrumbLabel) {
      return;
    }

    const label = this.obterLabelFinal(breadcrumbLabel, data);

    if (!this.deveAdicionarBreadcrumb(label, breadcrumbs)) {
      return;
    }

    const clickable = data['clickable'] !== false;
    const routerLink = this.obterRouterLinkParaBreadcrumb(data, url, clickable);

    breadcrumbs.push({
      label,
      routerLink,
      data: { showFullLabel: data['showFullLabel'] === true },
    });
  }

  private obterRouterLinkParaBreadcrumb(
    data: Record<string, unknown>,
    url: string,
    clickable: boolean,
  ): string | undefined {
    if (clickable) {
      return url;
    }

    const mode = data['breadcrumbEntityLinkMode'];
    if (!isBreadcrumbModoLinkEntidade(mode)) {
      return undefined;
    }

    return this.obterHandlersModoLinkEntidade(url)[mode]();
  }

  private obterHandlersModoLinkEntidade(
    url: string,
  ): Record<BreadcrumbModoLinkEntidade, () => string | undefined> {
    return {
      removerUltimoSegmento: () => this.removerUltimoSegmentoUrl(url),
      detalheEmpresaPeloRouter: () =>
        this.obterLinkEmpresaDetalhe(url, this.router.url),
      propostaDetalhesDesdeEdicao: () =>
        this.obterLinkPropostaDetalheDesdeEdicao(url),
    };
  }

  private removerUltimoSegmentoUrl(url: string): string | undefined {
    const parts = url.split('/').filter(this.filtrarSegmentosVazios);
    if (parts.length < 2) {
      return undefined;
    }
    parts.pop();
    return `/${parts.join('/')}`;
  }

  private obterLinkEmpresaDetalhe(
    entityBaseUrl: string,
    routerUrl: string,
  ): string | undefined {
    const path = routerUrl.split('?')[0];
    if (path.includes('/editar')) {
      return `${entityBaseUrl}/dashboard`;
    }
    return undefined;
  }

  private obterLinkPropostaDetalheDesdeEdicao(url: string): string {
    return url.replace(/\/editar\/?$/, '/detalhes');
  }

  private obterLabelFinal(
    label: string,
    data: Record<string, unknown>,
  ): string {
    const dynamicKey = data['dynamicBreadcrumbKey'] as string;
    const dynamicLabel = dynamicKey
      ? this.breadcrumbService.getLabel(dynamicKey)
      : null;

    return dynamicLabel ?? label;
  }

  private deveAdicionarBreadcrumb(
    label: string,
    breadcrumbs: MenuItem[],
  ): boolean {
    if (label === 'Carregando...') {
      return false;
    }

    return !this.breadcrumbJaExiste(breadcrumbs, label);
  }

  private breadcrumbJaExiste(breadcrumbs: MenuItem[], label: string): boolean {
    return breadcrumbs.some(this.compararLabel(label));
  }

  private compararLabel(labelBuscado: string): (b: MenuItem) => boolean {
    return (b: MenuItem) => b.label === labelBuscado;
  }

  private marcarUltimoItem(breadcrumbs: MenuItem[]): void {
    if (breadcrumbs.length === 0) {
      return;
    }

    const lastIndex = breadcrumbs.length - 1;
    breadcrumbs[lastIndex].routerLink = undefined;
    breadcrumbs[lastIndex]['data'] = {
      ...breadcrumbs[lastIndex]['data'],
      isLast: true,
    };
  }

  private atualizarEstado(breadcrumbs: MenuItem[]): void {
    this.items = breadcrumbs;
    this.cdr.markForCheck();
  }

  private filtrarSegmentosVazios(s: string): boolean {
    return !!s;
  }

  private obterUrlModulo(url: string): string {
    if (!url || url === '/') {
      return '';
    }
    const segments = url.split('/').filter(this.filtrarSegmentosVazios);
    if (segments.length > 0) {
      return `/${segments[0]}`;
    }
    return '';
  }
}
